import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useUser } from '../../context/UserContext';
import { getMissions, deleteMission, selectTransporteurForMission, getAvailableTransporteursForExpediteur, getMissionDocumentsForExpediteur, revertMissionToPending, uploadMissionDocumentForExpediteur } from '../../services/missionService';
import { getPendingOffers, getSavedOffers, saveOffer } from '../../services/messageService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PageHeader from '../../components/common/PageHeader';
import FilterCard from '../../components/common/FilterCard';
import { useI18n } from '../../i18n/I18nContext';
import { useFilter } from '../../context/FilterContext';
import './MiniTimeline.css';


const MissionList = () => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userType } = useUser();
  const isTransporteurLike = userType === 'transporteur' || userType === 'responsable_transporteur';
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sortBy, setSortBy] = useState('creation_desc');
  const [filterCreationDate, setFilterCreationDate] = useState('');
  const [filterDepartureDate, setFilterDepartureDate] = useState('');
  const [transporteurs, setTransporteurs] = useState([]);
  const [loadingTransporteurs, setLoadingTransporteurs] = useState(false);
  const [selectingMissionId, setSelectingMissionId] = useState(null);
  const [docsMissionId, setDocsMissionId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [selectedDocFile, setSelectedDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const { t } = useI18n();
  const { selectedTransporteurId } = useFilter();
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchMissions();
  }, [selectedTransporteurId]);

  useEffect(() => {
    if (userType === 'expediteur') {
      loadTransporteurs();
    }
  }, [userType]);

  useEffect(() => {
    if (selectingMissionId && userType === 'expediteur') {
      loadTransporteurs();
    }
  }, [selectingMissionId, userType]);

  const loadTransporteurs = async () => {
    try {
      setLoadingTransporteurs(true);
      const list = await getAvailableTransporteursForExpediteur();
      setTransporteurs(list);
    } catch (e) {
      setTransporteurs([]);
    } finally {
      setLoadingTransporteurs(false);
    }
  };

  useEffect(() => {
    if (docsMissionId) {
      (async () => {
        try {
          const res = await getMissionDocumentsForExpediteur(docsMissionId);
          setDocs(res || []);
        } catch (e) {
          setDocs([]);
        }
      })();
    } else {
      setDocs([]);
    }
  }, [docsMissionId]);

  const handleRowClick = (missionId) => {
    if (isTransporteurLike) {
      navigate(`/missions/assign?missionId=${missionId}`);
    } else {
      navigate(`/expediteur/missions/edit/${missionId}`);
    }
  };

  const fetchMissions = async () => {
    try {
      const data = await getMissions({ transporteurId: selectedTransporteurId });
      setMissions(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAssignClick = (e, missionId) => {
    e.stopPropagation();
    navigate(`/missions/assign?missionId=${missionId}`);
  };

  // Suppression de mission avec gestion d'erreur
  const handleDeleteMission = async (e, mission) => {
    e.stopPropagation();
    if (!window.confirm(t('missions.confirmDelete'))) return;
    try {
      // If transporteur is not the creator (client mission), revert accepted offer to pending and remove locally
      if (isTransporteurLike && mission.creatorType && String(mission.creatorType).toUpperCase() !== 'TRANSPORTEUR') {
        try {
          await revertMissionToPending(mission.id);
        } catch (_) {
          // If backend rejects PENDING, silently ignore to not block UX
        }
        setMissions(missions.filter(m => m.id !== mission.id));
        toast.success(t('missions.removedFromList'));
        return;
      }

      await deleteMission(mission.id);
      setMissions(missions.filter(m => m.id !== mission.id));
      toast.success(t('missions.deletedSuccess'));
    } catch (err) {
      if (err.isMissionInProgress) {
        toast.error(t('missions.cannotDeleteInProgress'));
      } else {
        toast.error(err.message || t('missions.deleteError'));
      }
    }
  };

  const filteredMissions = missions
    .filter(mission => {
      const matchesSearch =
        (mission.departureLocation && mission.departureLocation.toLowerCase().includes(search.toLowerCase())) ||
        (mission.arrivalLocation && mission.arrivalLocation.toLowerCase().includes(search.toLowerCase())) ||
        (mission.description && mission.description.toLowerCase().includes(search.toLowerCase()));
      const matchesStatus = status === 'all' || mission.status === status;
      const matchesCreation = !filterCreationDate || (mission.createdAt && mission.createdAt.startsWith(filterCreationDate));
      const matchesDeparture = !filterDepartureDate || (mission.departureDate && mission.departureDate.startsWith(filterDepartureDate));
      return matchesSearch && matchesStatus && matchesCreation && matchesDeparture;
    })
    .sort((a, b) => {
      if (sortBy === 'departure_desc') {
        return new Date(b.departureDate) - new Date(a.departureDate);
      } else if (sortBy === 'departure_asc') {
        return new Date(a.departureDate) - new Date(b.departureDate);
      } else if (sortBy === 'creation_desc') {
        return new Date(b.createdAt || b.id || 0) - new Date(a.createdAt || a.id || 0);
      }
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filteredMissions.length / PAGE_SIZE));
  const paginatedMissions = filteredMissions.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const renderMiniTimeline = (mission) => {
    const steps = [
      { label: t('missions.status.timeline.departure') || 'Départ', done: true },
      { label: t('missions.status.timeline.arrivalLoading') || 'Arrivée chargement', done: !!mission.loadingArrivalTime },
      { label: t('missions.status.timeline.departureLoading') || 'Départ chargement', done: !!mission.loadingDepartureTime },
      { label: t('missions.status.timeline.arrivalDelivery') || 'Arrivée livraison', done: !!mission.deliveryArrivalTime },
      { label: t('missions.status.timeline.completed') || 'Terminée', done: mission.status === 'TERMINEE' || mission.status === 'ANNULEE' }
    ];

    return (
      <div className="mini-timeline">
        {steps.map((step, idx) => {
          const isNext = !step.done && (idx === 0 || steps[idx - 1].done);
          return (
            <div
              key={idx}
              className={`mini-step ${step.done ? 'done' : ''} ${isNext ? 'active' : ''}`}
              data-label={step.label}
            >
              <div className="mini-dot"></div>
              {idx < steps.length - 1 && <div className="mini-line"></div>}
            </div>
          );
        })}
      </div>
    );
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger">{error}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container-fluid">
        <PageHeader
          title={t('missions.missionList.title')}
          subtitle={t('missions.missionList.subtitle')}
          buttonText={['expediteur', 'transporteur', 'responsable_transporteur'].includes(userType) ? t('missions.missionList.newMission') : ""}
          buttonTo={userType === 'expediteur' ? "/expediteur/missions/create" : "/missions/create"}
        />

        <FilterCard>
          <div className="row g-3 align-items-end mb-3">
            <div className="col-md-5">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('missions.missionList.searchPlaceholder')}
                  value={search}
                  onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={status}
                onChange={e => { setStatus(e.target.value); setCurrentPage(1); }}
              >
                <option value="all">{t('missions.missionList.allStatus')}</option>
                <option value="EN_ATTENTE">{t('missions.status.pending')}</option>
                <option value="EN_COURS">{t('missions.status.in_progress')}</option>
                <option value="TERMINEE">{t('missions.status.completed')}</option>
                <option value="ANNULEE">{t('missions.status.cancelled')}</option>
              </select>
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={sortBy}
                onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
              >
                <option value="creation_desc">Tri par date de création (plus récente → plus ancienne)</option>
                <option value="departure_desc">Tri par date de départ (plus récente)</option>
                <option value="departure_asc">Tri par date de départ (plus ancienne)</option>
              </select>
            </div>
          </div>
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <label className="form-label text-muted small mb-1">Filtrer par date de création</label>
              <input 
                type="date" 
                className="form-control" 
                value={filterCreationDate}
                onChange={e => { setFilterCreationDate(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label text-muted small mb-1">Filtrer par date de départ</label>
              <input 
                type="date" 
                className="form-control" 
                value={filterDepartureDate}
                onChange={e => { setFilterDepartureDate(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>
        </FilterCard>

        {/* Transporteur: Table view | Expediteur: Card view */}
        {isTransporteurLike ? (
          <div className="c-table-card card">
            <div className="card-body">
              {filteredMissions.length === 0 ? (
                <div className="text-center p-5 text-muted">
                  {t('missions.missionList.noMissionsFound')}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="c-table table-hover">
                    <thead>
                      <tr>
                        <th>{t('missions.missionList.mission')}</th>
                        <th>{t('missions.missionList.date')}</th>
                        {isTransporteurLike && <th>{t('client.donneurDordre')}</th>}
                        {/* Common status column */}
                        <th>{t('missions.missionList.status')}</th>
                        <th>Progression</th>
                        {/* Transporteur-only columns */}
                        {isTransporteurLike && <th>{t('common.chauffeurs')}</th>}
                        {isTransporteurLike && <th>{t('missions.missionList.assignMission')}</th>}
                        <th>{t('missions.missionList.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedMissions.map((mission) => (
                        <tr
                          key={mission.id}
                          onClick={() => handleRowClick(mission.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td data-label={t('missions.missionList.mission')}>
                            {mission.expediteurNom || mission.destinataireNom ? (
                              <div className="d-flex flex-column">
                                <span className="fw-bold">{mission.expediteurNom || mission.departureLocation} → {mission.destinataireNom || mission.arrivalLocation}</span>
                                <span className="text-muted small fw-light">{mission.departureLocation} → {mission.arrivalLocation}</span>
                              </div>
                            ) : (
                              <>{mission.departureLocation} → {mission.arrivalLocation}</>
                            )}
                          </td>
                          <td data-label={t('missions.missionList.date')}>
                            {new Date(mission.departureDate).toLocaleDateString()}
                          </td>
                          {isTransporteurLike && (
                            <td data-label={t('client.donneurDordre')}>
                              {mission.clientNom ? `${mission.clientPrenom || ''} ${mission.clientNom}` : '-'}
                            </td>
                          )}
                          {/* Common status */}
                          <td data-label={t('missions.missionList.status')}>
                            <span className={`badge rounded-pill ${mission.status === 'EN_ATTENTE' ? 'bg-warning text-dark' :
                              mission.status === 'EN_COURS' ? 'bg-primary' :
                                mission.status === 'TERMINEE' ? 'bg-success' :
                                  mission.status === 'ANNULEE' ? 'bg-danger' :
                                    'bg-secondary'
                              }`}>
                              {t(`missions.status.${mission.status.toLowerCase()}`)}
                            </span>
                          </td>
                          <td data-label="Progression">
                            {renderMiniTimeline(mission)}
                          </td>
                          {/* Transporteur-only */}
                          {isTransporteurLike && (
                            <>
                              <td data-label={t('common.chauffeurs')}>
                                {mission.assignedChauffeurName || t('missions.missionList.notAssigned')}
                              </td>
                              <td data-label={t('missions.missionList.assignMission')}>
                                {isTransporteurLike && mission.status === 'EN_ATTENTE' && !mission.affectationMissionId && (
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={(e) => handleAssignClick(e, mission.id)}
                                  >
                                    <i className="bi bi-person-plus me-1"></i> {t('missions.missionList.assign')}
                                  </button>
                                )}
                                {isTransporteurLike && mission.status === 'EN_ATTENTE' && (
                                  <button
                                    className="btn btn-sm btn-outline-primary ms-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/subcontract?missionId=${mission.id}`);
                                    }}
                                  >
                                    <i className="bi bi-share me-1"></i> {t('missions.missionList.subcontract')}
                                  </button>
                                )}
                                {isTransporteurLike && mission.affectationMissionId && (
                                  <span className="badge bg-info text-white">
                                    <i className="bi bi-check-circle me-1"></i> {t('missions.missionList.assigned')}
                                  </span>
                                )}
                              </td>
                            </>
                          )}
                          <td data-label={t('missions.missionList.actions')}>
                            {/* Common: delete when EN_ATTENTE */}
                            {['expediteur', 'transporteur', 'responsable_transporteur'].includes(userType) && mission.status === 'EN_ATTENTE' && (
                              <button
                                onClick={(e) => handleDeleteMission(e, mission)}
                                className="btn btn-sm btn-danger me-2"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                            {/* Transporteur-specific extra action (placeholder accept) */}
                            {userType === 'transporteur' && mission.status === 'PENDING' && (
                              <button
                                onClick={(e) => e.stopPropagation()}
                                className="btn btn-sm btn-success me-2"
                              >
                                <i className="bi bi-check-lg"></i> {t('missions.missionList.accept')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {filteredMissions.length > PAGE_SIZE && (
                <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                  <small className="text-muted">
                    {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredMissions.length)} sur {filteredMissions.length} missions
                  </small>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>‹</button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, idx) =>
                          p === '...' ? (
                            <li key={`dots-${idx}`} className="page-item disabled"><span className="page-link">…</span></li>
                          ) : (
                            <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                            </li>
                          )
                        )}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>›</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {filteredMissions.length === 0 ? (
              <div className="text-center p-5 text-muted">{t('missions.missionList.noMissionsFound')}</div>
            ) : (
              filteredMissions.map((mission) => (
                <div className="col-12 col-md-6 col-lg-4" key={mission.id}>
                  <div
                    className="card h-100 border-0"
                    style={{ borderRadius: '1.25rem', boxShadow: '0 0.625rem 1.875rem rgba(0,0,0,0.08)', overflow: 'hidden', cursor: 'pointer' }}
                    onClick={() => navigate(`/expediteur/missions/edit/${mission.id}`)}
                  >
                    <div style={{
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                      color: 'white',
                      padding: '0.9rem 1.1rem'
                    }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-start gap-2">
                          <i className="bi bi-geo-alt" style={{ fontSize: '1.1rem', marginTop: '2px' }}></i>
                          <div>
                            {mission.expediteurNom || mission.destinataireNom ? (
                              <div className="d-flex flex-column">
                                <span className="fw-bold">{mission.expediteurNom || mission.departureLocation} → {mission.destinataireNom || mission.arrivalLocation}</span>
                                <span className="text-white-50 small fw-light">{mission.departureLocation} → {mission.arrivalLocation}</span>
                              </div>
                            ) : (
                              <span className="fw-semibold">{mission.departureLocation} → {mission.arrivalLocation}</span>
                            )}
                          </div>
                        </div>
                        <span className={`badge rounded-pill ${mission.status === 'EN_ATTENTE' ? 'bg-warning text-dark' :
                          mission.status === 'EN_COURS' ? 'bg-light text-dark' :
                            mission.status === 'TERMINEE' ? 'bg-success' :
                              mission.status === 'ANNULEE' ? 'bg-danger' : mission.status === 'TERMINEE' || mission.status === 'ANNULEE' ? 'bg-success' : 'bg-secondary'
                          }`}>
                          {t(`missions.status.${mission.status.toLowerCase()}`)}
                        </span>
                      </div>
                      <div className="mt-2 text-white">
                        {renderMiniTimeline(mission)}
                      </div>
                    </div>
                    <div className="card-body d-flex flex-column" style={{ padding: '1.1rem' }}>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <span className="badge rounded-pill bg-light text-dark border"><i className="bi bi-calendar-event me-1"></i>{new Date(mission.departureDate).toLocaleDateString()}</span>
                        <span className="badge rounded-pill bg-light text-dark border"><i className="bi bi-box-seam me-1"></i>{mission.cargoType}</span>
                        <span className="badge rounded-pill bg-light text-dark border"><i className="bi bi-truck me-1"></i>{mission.vehicleType}</span>
                        <span className="badge rounded-pill bg-light text-dark border"><i className="bi bi-speedometer2 me-1"></i>{mission.weight} t</span>
                        {mission.affectationMissionId && (
                          <span className="badge rounded-pill bg-info text-white border">
                            <i className="bi bi-check-circle me-1"></i>{t('missions.missionList.assigned')}
                          </span>
                        )}
                      </div>
                      <div className="mt-auto d-flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                        {/* Delete if EN_ATTENTE */}
                        {mission.status === 'EN_ATTENTE' && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={(e) => handleDeleteMission(e, mission)}
                          >
                            <i className="bi bi-trash me-1"></i> {t('missions.missionList.delete')}
                          </button>
                        )}
                        {/* Send to transporteur */}
                        <button
                          className="btn btn-sm btn-outline-success"
                          disabled={mission.status !== 'EN_ATTENTE' || mission.affectationMissionId}
                          onClick={(e) => { e.stopPropagation(); setSelectingMissionId(mission.id); }}
                        >
                          <i className="bi bi-send me-1"></i> {t('missions.missionList.send')}
                        </button>
                        {/* Docs */}
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => { e.stopPropagation(); setDocsMissionId(mission.id); }}
                        >
                          <i className="bi bi-file-earmark-text me-1"></i> {t('missions.missionList.docs')}
                        </button>
                        {/* Track button removed: tracking available on dashboard map */}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {/* Select Transporteur Modal */}
      {selectingMissionId && (
        <div className="modal d-block" tabIndex="-1" onClick={() => setSelectingMissionId(null)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('missions.missionList.selectTransporteur')}</h5>
                <button type="button" className="btn-close" onClick={() => setSelectingMissionId(null)}></button>
              </div>
              <div className="modal-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">{t('common.chooseTransporteur')}</span>
                  <button className="btn btn-sm btn-outline-secondary" onClick={loadTransporteurs} disabled={loadingTransporteurs}>
                    {loadingTransporteurs ? t('common.loading') : t('common.refresh')}
                  </button>
                </div>
                {transporteurs.length === 0 ? (
                  <div className="text-muted">
                    {t('missions.missionList.noTransporteurs')}
                    <div className="mt-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setSelectingMissionId(null);
                          navigate('/expediteur/transporteurs');
                        }}
                      >
                        {t('common.addTransporteur')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <ul className="list-group">
                    {transporteurs.map(tr => (
                      <li key={tr.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-semibold">{tr.enterpriseName || tr.entrepriseName || tr.companyName || tr.raisonSociale || tr.raison_sociale || [tr.firstname, tr.lastname].filter(Boolean).join(' ') || tr.name || t('common.enterpriseFallback', { id: tr.id })}</div>
                          <div className="small text-muted">
                            {[tr.city, tr.country].filter(Boolean).join(', ')} {tr.telephone ? `· ${tr.telephone}` : ''}
                          </div>
                        </div>
                        <button className="btn btn-sm btn-primary" onClick={async () => {
                          try {
                            await selectTransporteurForMission(selectingMissionId, tr.id);
                            setSelectingMissionId(null);
                            fetchMissions();
                            toast.success(t('missions.missionList.missionSent'));
                            navigate(`/expediteur/missions/${selectingMissionId}/contract?transporteurId=${tr.id}`);
                          } catch (e) {
                            toast.error(e.message);
                          }
                        }} style={{ height: '6.25rem' }}
                        >{t('common.select')}</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {docsMissionId && (
        <div className="modal d-block" tabIndex="-1" onClick={() => setDocsMissionId(null)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('missions.missionList.documents')}</h5>
                <button type="button" className="btn-close" onClick={() => setDocsMissionId(null)}></button>
              </div>
              <div className="modal-body">
                {userType === 'expediteur' && (
                  <div className="mb-3 d-flex gap-2 align-items-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="file"
                      className="form-control"
                      onChange={(e) => setSelectedDocFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                    />
                    <button
                      className="btn btn-primary"
                      disabled={!selectedDocFile || uploadingDoc}
                      onClick={async () => {
                        try {
                          setUploadingDoc(true);
                          await uploadMissionDocumentForExpediteur(docsMissionId, selectedDocFile);
                          const res = await getMissionDocumentsForExpediteur(docsMissionId);
                          setDocs(res || []);
                          setSelectedDocFile(null);
                          toast.success(t('common.uploaded'));
                        } catch (e) {
                          toast.error(e.message || t('common.error'));
                        } finally {
                          setUploadingDoc(false);
                        }
                      }}
                    >
                      {uploadingDoc ? t('common.uploading') : t('common.upload')}
                    </button>
                  </div>
                )}
                {docs.length === 0 ? (
                  <div className="text-muted">{t('missions.missionList.noDocuments')}</div>
                ) : (
                  <ul className="list-group">
                    {docs.map((d, idx) => {
                      const type = (d.type || '').toLowerCase();
                      let label = d.name || d.filename || t('missions.missionList.document');
                      if (!d.name) {
                        if (type === 'delivery_photo' || type === 'delivery_proof') {
                          label = t('missions.missionList.deliveryProof') || 'Preuve de livraison';
                        } else if (type === 'goods_photo') {
                          label = t('missions.missionList.cargoPhotos') || 'Photos marchandise arrivée';
                        }
                      }
                      return (
                        <li key={d.id || idx} className="list-group-item d-flex justify-content-between align-items-center">
                          <span>{label}</span>
                          {d.url && (
                            <a href={d.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary">
                              {t('missions.missionList.open')}
                            </a>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MissionList;